import React from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { LangSwitcher } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import logo from "@/assets/eii-logo.jpg";
import { Stethoscope, GraduationCap, LogIn } from "lucide-react";

const HERO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_equi-integral-1/artifacts/uf4r5prx_IMG_6391.webp";

const FOOTER = [
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_equi-integral-1/artifacts/3no1ojbc_473592386_122151664136337179_3481186472222611371_n.webp", key: "footer_riding" },
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_equi-integral-1/artifacts/hicl24ls_IMG_3955.webp", key: "footer_dressage" },
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_equi-integral-1/artifacts/42s9ltoy_IMG_0798.webp", key: "footer_stable" },
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_equi-integral-1/artifacts/qi4zfnvl_IMG_5446.webp", key: "footer_farrier" },
];

export default function Home() {
  const { t } = useApp();
  const nav = useNavigate();

  return (
    <div className="min-h-screen">
      <div className="absolute right-4 top-4 z-20 sm:right-6 sm:top-6">
        <LangSwitcher />
      </div>

      <section className="relative flex min-h-[88vh] items-center overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/20" />
        <div className="relative z-10 mx-auto w-full max-w-5xl px-6 py-16">
          <div className="max-w-2xl animate-fade-up">
            <div className="mb-6 flex items-center gap-4">
              <img src={logo} alt="Logo" className="h-16 w-16 rounded-full bg-white/90 object-cover p-1" />
              <span className="text-xs uppercase tracking-[0.25em] text-white/80">Equi-Integral-International</span>
            </div>
            <h1 className="font-serif-h text-4xl font-light leading-tight text-white sm:text-5xl lg:text-6xl">
              {t("tagline")}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg">
              {t("hero_sub")}
            </p>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button
                data-testid="cta-consultation"
                onClick={() => nav("/consultation")}
                className="rounded-full bg-primary px-7 py-6 text-base font-medium text-primary-foreground shadow-lg transition-transform hover:-translate-y-0.5 hover:bg-[#2C4633]"
              >
                <Stethoscope className="mr-2 h-5 w-5" strokeWidth={1.5} />
                {t("book_consultation")}
              </Button>
              <Button
                data-testid="cta-riding"
                onClick={() => nav("/riding-lesson")}
                className="rounded-full bg-[#FBF3E7] px-7 py-6 text-base font-medium text-[#1A2C21] shadow-lg transition-transform hover:-translate-y-0.5 hover:bg-[#f3e6d0]"
              >
                <GraduationCap className="mr-2 h-5 w-5" strokeWidth={1.5} />
                {t("book_riding")}
              </Button>
              <Button
                data-testid="cta-login"
                variant="outline"
                onClick={() => nav("/login")}
                className="rounded-full border-white/40 bg-white/10 px-7 py-6 text-base font-medium text-white backdrop-blur transition-transform hover:-translate-y-0.5 hover:bg-white/20"
              >
                <LogIn className="mr-2 h-5 w-5" strokeWidth={1.5} />
                {t("nav_login")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-16">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FOOTER.map((f) => (
            <div
              key={f.key}
              data-testid={`footer-photo-${f.key}`}
              className="overflow-hidden rounded-[20px] border border-border bg-card shadow-[0_8px_30px_rgba(62,97,72,0.06)]"
            >
              <div className="flex h-56 items-center justify-center bg-[#EAF0EB]">
                <img src={f.url} alt={t(f.key)} className="max-h-56 w-full object-contain" />
              </div>
            </div>
          ))}
        </div>
        <p className="mt-12 text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} Equi-Integral-International
        </p>
      </section>
    </div>
  );
}
